function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from "react";
import { withRouter } from "react-router";
import { Card, Pill, Empty, Pending } from "../../components/analytics-kit";
import Error from "../../components/error";
import Auth from "../../functions/authenticate";
import { num } from "../sales/territory-common";
import { TIMEZONES, tzCity, browserTz, tzOffsetMinutes, asUtcDate } from "../reports/schedule-sheet";
import DataShell, { dataSvc, safe, Btn, Notice } from "./shell";
import { isCheckRun, isRunning, isEmptyRun, COUNTLESS, feedHealth } from "./feed-health";

/*
 * Feeds (kit, rebuilt 2026-10-09 to the dataflow mockup "Main"). Same rails as
 * views/data-feeds/index.jsx (legacy page kept for the two gated tenants):
 * /v1/data-feeds providers · schedules · runs · validate · schedules/{id}/run.
 *
 * Layout: a status summary card (how many feeds are fine, which one needs a look and why),
 * a two-column grid of feed cards (health dot, plain name, where/when/what in one sentence,
 * last-run line, "···" menu), then Recent runs with a plain "what changed" sentence per run.
 * Everything reads from the customer's side: no mode names, hostnames or raw run messages
 * (the raw message rides the row's tooltip). Schedule times are converted from the feed's own
 * zone to the browser's and named by city ("New York time").
 *
 * Passwords are never echoed back; a blank password on edit keeps the current one.
 * Pause/Resume re-posts the schedule unchanged except for its status.
 */
const svc = api => dataSvc(`/data-feeds${api}`);
const parseSettings = row => {
  try {
    return JSON.parse(row.settings || "{}");
  } catch (e) {
    return {};
  }
};
const RUNS_TOP = 100; // enough history to find each feed's last run; the table shows 30
const RUNS_SHOWN = 30;

// ── Plain names and nouns per feed type ──
const FEED_NAMES = {
  SalesTransaction: "Sales orders",
  Customer: "Customers",
  Company: "Companies",
  Product: "Products",
  SalesRep: "Sales reps",
  Audience: "Audience list",
  Inventory: "Inventory",
  Shopify: "Online store",
  HistoricalImport: "Website history",
  Api: "Business system"
};
const NOUNS = {
  SalesTransaction: ["order line", "order lines"],
  Customer: ["customer", "customers"],
  Company: ["company", "companies"],
  Product: ["product", "products"],
  SalesRep: ["rep", "reps"],
  Audience: ["person", "people"],
  Inventory: ["stock record", "stock records"],
  Shopify: ["record", "records"],
  HistoricalImport: ["row of history", "rows of history"],
  Api: ["record", "records"]
};
const feedName = (providers, name) => {
  if (FEED_NAMES[name]) return FEED_NAMES[name];
  const p = (providers || []).find(x => x.name === name);
  return p && p.displayName || name || "Feed";
};
const noun = (provider, n) => {
  const pair = NOUNS[provider] || ["row", "rows"];
  return n === 1 ? pair[0] : pair[1];
};
const counted = (provider, n) => `${num(n)} ${noun(provider, n)}`;

// Where the file comes from, from the customer's side.
function sourceText(row, s) {
  if (row.provider === "Shopify") return "your online store";
  if (row.provider === "HistoricalImport") return "Google Analytics";
  if (row.provider === "Api") return "your business system";
  const url = String(s.Url || s.url || "");
  return /plunk/i.test(url) ? "Plunk file storage" : "your file storage";
}

// What each run does, in plain words (by the feed's mode, or the provider's default).
function modeText(provider, mode, s) {
  const many = noun(provider, 2);
  switch (mode) {
    case "refresh":
    case "full-refresh":
      if (provider === "Product") return "Replaces the whole catalog each run.";
      if (provider === "Shopify") return "Brings in the store's latest products, orders and customers each run.";
      if (provider === "HistoricalImport") return "Brings in past website visits from before Plunk started counting.";
      return "Replaces everything each run.";
    case "upsert":
      return `Adds new ${many} and updates existing ones.`;
    case "upsert+deactivate-missing":
      return `Adds and updates ${many}, and turns off ${many} no longer in the file.`;
    case "window-refresh":
      {
        const days = Number(s.WindowDays || s.windowDays) || 0;
        return days ? `Replaces the last ${days} days each run and keeps older history.` : "Replaces recent days each run and keeps older history.";
      }
    case "append":
      return `Adds new ${many} each run and leaves existing ones alone.`;
    case "membership-add":
      return s.ReferenceId ? `Adds people to the "${s.ReferenceId}" audience.` : "Adds people to an audience.";
    case "discover":
    case "preview":
      return "Checks the source without changing anything.";
    default:
      return "";
  }
}
// Short labels for the form's "Each run" picker.
const MODE_LABELS = {
  "refresh": "Replace everything",
  "full-refresh": "Replace everything",
  "upsert": "Add new and update existing",
  "upsert+deactivate-missing": "Add, update, and turn off missing ones",
  "window-refresh": "Replace recent days, keep older history",
  "append": "Add new only",
  "membership-add": "Add people to an audience",
  "discover": "Check the source only",
  "preview": "Check the source only",
  "key-events": "Check the source only"
};

// ── Times ──
const timeOf = d => d.toLocaleTimeString("en-US", {
  hour: "numeric",
  minute: "2-digit"
});
const sameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
function dayWord(d) {
  const now = new Date();
  const y = new Date(now);
  y.setDate(now.getDate() - 1);
  if (sameDay(d, now)) return "Today";
  if (sameDay(d, y)) return "Yesterday";
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: d.getFullYear() === now.getFullYear() ? undefined : "numeric"
  });
}
/** Table cell: "Today 9:40 AM" · "Oct 8 11:00 PM". */
function whenCell(v) {
  const d = asUtcDate(v);
  if (!d || isNaN(d.getTime())) return "";
  return `${dayWord(d)} ${timeOf(d)}`;
}
/** In a sentence: "today at 9:40 AM" · "on Oct 8 at 11:00 PM". */
function whenPhrase(v) {
  const d = asUtcDate(v);
  if (!d || isNaN(d.getTime())) return "";
  const w = dayWord(d);
  return w === "Today" || w === "Yesterday" ? `${w.toLowerCase()} at ${timeOf(d)}` : `on ${w} at ${timeOf(d)}`;
}
const ordinal = n => {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};
const hourLabel = h => {
  const x = new Date(2000, 0, 1, Number(h) || 0, 0);
  return timeOf(x);
};

/**
 * The schedule in the browser's zone: the stored hour is local to the feed's own zone
 * (the worker honors it), so convert through UTC. Weekly = Mondays, monthly = the 1st,
 * anything else runs daily (the worker's cron rules).
 */
function scheduleText(row) {
  const feedTz = row.timezone || "America/New_York";
  const here = browserTz();
  const now = new Date();
  const freq = String(row.frequency || "Daily").toLowerCase();
  let y = now.getFullYear();
  let m = now.getMonth();
  let d = now.getDate();
  if (freq === "weekly") {
    const add = (8 - now.getDay()) % 7;
    const t = new Date(y, m, d + add);
    y = t.getFullYear();
    m = t.getMonth();
    d = t.getDate();
  }
  if (freq === "monthly") {
    d = 1;
  }
  const guess = new Date(Date.UTC(y, m, d, Number(row.hour) || 0, 0));
  const at = new Date(guess.getTime() - tzOffsetMinutes(feedTz, guess) * 60000);
  const time = at.toLocaleTimeString("en-US", {
    timeZone: here,
    hour: "numeric",
    minute: "2-digit"
  });
  const city = tzCity(here);
  if (freq === "weekly") return `every ${at.toLocaleDateString("en-US", {
    timeZone: here,
    weekday: "long"
  })} at ${time} ${city}`;
  if (freq === "monthly") {
    const dom = Number(at.toLocaleDateString("en-US", {
      timeZone: here,
      day: "numeric"
    }));
    return dom >= 28 ? `on the last day of each month at ${time} ${city}` : `on the ${ordinal(dom)} of each month at ${time} ${city}`;
  }
  return `every day at ${time} ${city}`;
}
// Run reading (shared with Connect): see ./feed-health

function runResult(r) {
  if (isRunning(r)) return ["info", "Running"];
  if (r.success === false) return ["danger", "Didn't finish"];
  if (isCheckRun(r)) return ["neutral", "Checked"];
  if (isEmptyRun(r)) return ["warning", "Empty file"];
  return ["success", "Done"];
}

/** One plain sentence for a run's counts and message. Never the raw message. */
function whatChanged(r) {
  const p = r.provider;
  const up = Number(r.rowsUpserted) || 0;
  const del = Number(r.rowsDeleted) || 0;
  const msg = String(r.message || "");
  if (isRunning(r)) return "Still going";
  if (r.success === false) return "Nothing changed. Hover for the details.";
  if (isCheckRun(r)) return "Checked the source, nothing changed";
  if (isEmptyRun(r)) return `No ${noun(p, 2)} in the file, nothing changed`;
  switch (p) {
    case "Customer":
    case "Company":
      {
        const m = msg.match(/created (\d+)/);
        const created = m ? Number(m[1]) : 0;
        return `${counted(p, up)} updated, ${created ? `${num(created)} new ${created === 1 ? "person" : "people"} added to your contacts` : "no new ones"}`;
      }
    case "SalesTransaction":
      return `${counted(p, up)} from the past year now on file${del ? `, after replacing ${num(del)}` : ""}`;
    case "SalesRep":
      return `${counted(p, up)} updated${del ? `, ${num(del)} turned off` : ""}`;
    case "Product":
      return `${counted(p, up)} in the catalog`;
    case "Inventory":
      return `${counted(p, up)} updated`;
    case "Audience":
      {
        const m = msg.match(/uploaded to '([^']+)'/);
        return m ? `People sent to the "${m[1]}" audience` : "People sent to the audience";
      }
    case "Shopify":
      return "The store's latest data came in";
    case "HistoricalImport":
      if (/nothing to import|no rows/i.test(msg)) return "Nothing new to bring in";
      return up ? `${counted(p, up)} brought in` : "Past website visits brought in";
    default:
      return `${counted(p, up)} updated${del ? `, ${num(del)} removed` : ""}`;
  }
}

/** "···" menu (copied from views/reports/index.jsx; pop is position:fixed). */
class RowMenu extends React.Component {
  constructor(...args) {
    super(...args);
    _defineProperty(this, "state", {
      open: false,
      pos: null
    });
    _defineProperty(this, "onDoc", e => {
      if (this._wrap && !this._wrap.contains(e.target)) this.close();
    });
    _defineProperty(this, "onKeyDoc", e => {
      if (e.key === "Escape") this.close();
    });
    _defineProperty(this, "close", () => {
      if (this.state.open) {
        this.unbind();
        this.setState({
          open: false
        });
      }
    });
    _defineProperty(this, "toggle", e => {
      e.stopPropagation();
      if (this.state.open) {
        this.close();
        return;
      }
      const r = e.currentTarget.getBoundingClientRect();
      const below = window.innerHeight - r.bottom > 260;
      this.setState({
        open: true,
        pos: below ? {
          top: r.bottom + 6,
          right: window.innerWidth - r.right
        } : {
          bottom: window.innerHeight - r.top + 6,
          right: window.innerWidth - r.right
        }
      });
      this.bind();
    });
  }
  componentWillUnmount() {
    this.unbind();
  }
  bind() {
    document.addEventListener("mousedown", this.onDoc);
    document.addEventListener("keydown", this.onKeyDoc);
    window.addEventListener("scroll", this.close, true);
    window.addEventListener("resize", this.close);
  }
  unbind() {
    document.removeEventListener("mousedown", this.onDoc);
    document.removeEventListener("keydown", this.onKeyDoc);
    window.removeEventListener("scroll", this.close, true);
    window.removeEventListener("resize", this.close);
  }
  render() {
    const {
      label,
      items
    } = this.props;
    const {
      open,
      pos
    } = this.state;
    const list = (items || []).filter(Boolean);
    if (!list.length) return null;
    return /*#__PURE__*/React.createElement("span", {
      className: "df-menu",
      ref: el => {
        this._wrap = el;
      },
      onClick: e => e.stopPropagation()
    }, /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "df-menu-btn",
      "aria-label": `More for ${label}`,
      "aria-haspopup": "menu",
      "aria-expanded": open,
      onClick: this.toggle
    }, "\xB7\xB7\xB7"), open ? /*#__PURE__*/React.createElement("div", {
      className: "df-menu-pop",
      role: "menu",
      style: pos
    }, list.map(it => /*#__PURE__*/React.createElement("button", {
      key: it.label,
      type: "button",
      role: "menuitem",
      className: it.danger ? "danger" : "",
      disabled: it.disabled,
      onClick: () => {
        this.close();
        it.onClick && it.onClick();
      }
    }, it.label))) : null);
  }
}
const FREQUENCIES = [["Daily", "Every day"], ["Weekly", "Every Monday"], ["Monthly", "On the 1st of each month"]];
const emptyForm = () => ({
  id: null,
  provider: "",
  url: "",
  username: "",
  password: "",
  mode: "",
  frequency: "Daily",
  hour: 4,
  timezone: browserTz(),
  status: "Active"
});
class FeedsView extends React.Component {
  constructor(...args) {
    super(...args);
    _defineProperty(this, "state", {
      providers: null,
      schedules: null,
      runs: [],
      error: false,
      form: null,
      saving: false,
      validating: false,
      validation: null,
      running: {},
      toggling: {},
      notice: null,
      confirmDelete: null,
      audiences: null,
      runsFor: null
    });
    _defineProperty(this, "reload", () => this.setState({
      error: false
    }, () => this.load()));
    _defineProperty(this, "startAdd", () => this.setState({
      form: emptyForm(),
      validation: null
    }));
    _defineProperty(this, "startEdit", row => {
      const s = parseSettings(row);
      this.setState({
        form: {
          id: row.id,
          provider: row.provider,
          url: s.Url || s.url || "",
          username: s.Username || "",
          password: "",
          mode: row.mode,
          frequency: row.frequency,
          hour: row.hour,
          timezone: row.timezone || "America/New_York",
          status: row.status,
          referenceId: s.ReferenceId || "",
          _hadPassword: !!s.Password
        },
        validation: null
      }, () => {
        try {
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
        } catch (e) {/* old browsers */}
      });
    });
    _defineProperty(this, "field", k => e => this.setState({
      form: {
        ...this.state.form,
        [k]: e.target.value
      }
    }));
    _defineProperty(this, "save", async () => {
      const f = this.state.form;
      if (!f || this.state.saving || !f.provider || !f.url) return;
      const row = (this.state.schedules || []).find(s => s.id === f.id);
      const settings = {
        ...(row ? parseSettings(row) : {}),
        Url: f.url
      };
      if (f.username) settings.Username = f.username;
      if (f.password) settings.Password = f.password;
      if (f.referenceId) settings.ReferenceId = f.referenceId;else delete settings.ReferenceId;
      this.setState({
        saving: true
      });
      try {
        await svc("/schedules").postAsync({
          id: f.id || undefined,
          provider: f.provider,
          settings: JSON.stringify(settings),
          mode: f.mode,
          frequency: f.frequency,
          hour: Number(f.hour) || 0,
          timezone: f.timezone || "America/New_York",
          status: f.status
        });
        this.setState({
          saving: false,
          form: null,
          notice: "Feed saved."
        });
        this.reload();
      } catch (e) {
        this.setState({
          saving: false,
          notice: "The feed couldn't be saved. Check the details and try again."
        });
      }
    });
    _defineProperty(this, "validate", async () => {
      const f = this.state.form;
      if (!f || !f.url || this.state.validating) return;
      this.setState({
        validating: true,
        validation: null
      });
      try {
        const res = await svc("/validate").postAsync({
          url: f.url,
          username: f.username || null,
          password: f.password || null,
          provider: f.provider || null
        });
        this.setState({
          validating: false,
          validation: res
        });
      } catch (e) {
        this.setState({
          validating: false,
          validation: {
            valid: false,
            error: "The file couldn't be checked right now. Try again in a minute."
          }
        });
      }
    });
    _defineProperty(this, "runNow", async row => {
      if (this.state.running[row.id]) return;
      const name = feedName(this.state.providers, row.provider);
      this.setState({
        running: {
          ...this.state.running,
          [row.id]: true
        },
        notice: null
      });
      try {
        await svc(`/schedules/${row.id}/run`).postAsync({});
        this.setState({
          notice: `${name} is starting. Its result shows under Recent runs when it finishes.`
        });
        this.later(4000);
        this.later(30000);
      } catch (e) {
        this.setState({
          notice: `${name} couldn't be started. Try again in a minute.`
        });
      }
      if (this._alive) this.setState({
        running: {
          ...this.state.running,
          [row.id]: false
        }
      });
    });
    _defineProperty(this, "togglePause", async row => {
      if (this.state.toggling[row.id]) return;
      const name = feedName(this.state.providers, row.provider);
      const next = row.status === "Active" ? "Inactive" : "Active";
      this.setState({
        toggling: {
          ...this.state.toggling,
          [row.id]: true
        },
        notice: null
      });
      try {
        await svc("/schedules").postAsync({
          id: row.id,
          provider: row.provider,
          settings: row.settings,
          mode: row.mode,
          frequency: row.frequency,
          hour: row.hour,
          timezone: row.timezone,
          status: next
        });
        this.setState({
          notice: next === "Active" ? `${name} is back on its schedule.` : `${name} is paused. It won't run until you resume it.`
        });
        this.load();
      } catch (e) {
        this.setState({
          notice: `${name} couldn't be ${next === "Active" ? "resumed" : "paused"}. Try again in a minute.`
        });
      }
      if (this._alive) this.setState({
        toggling: {
          ...this.state.toggling,
          [row.id]: false
        }
      });
    });
    _defineProperty(this, "remove", async row => {
      try {
        await svc("/schedules").deleteAsync(row.id);
        this.setState({
          confirmDelete: null,
          notice: "Feed deleted.",
          runsFor: this.state.runsFor === row.id ? null : this.state.runsFor
        });
        this.reload();
      } catch (e) {
        this.setState({
          notice: "The feed couldn't be deleted. Try again in a minute.",
          confirmDelete: null
        });
      }
    });
    _defineProperty(this, "showRuns", id => this.setState({
      runsFor: id
    }, () => {
      if (this._runsEl) {
        try {
          this._runsEl.scrollIntoView({
            behavior: "smooth",
            block: "start"
          });
        } catch (e) {
          this._runsEl.scrollIntoView();
        }
      }
    }));
  }
  async componentDidMount() {
    this._alive = true;
    if (Auth.isAuthenticated() || (await Auth.attachOrSignIn())) this.load();
  }
  componentWillUnmount() {
    this._alive = false;
    (this._timers || []).forEach(clearTimeout);
  }
  async load() {
    try {
      const [providers, schedules, runs] = await Promise.all([svc("/providers").getAsync(), svc("/schedules").getAsync(), safe(svc(`/runs?top=${RUNS_TOP}`).getAsync(), [])]);
      if (this._alive) this.setState({
        providers: providers || [],
        schedules: schedules || [],
        runs: runs || []
      });
    } catch (e) {
      if (this._alive) this.setState({
        error: true
      });
    }
  }
  later(ms) {
    this._timers = (this._timers || []).concat(setTimeout(() => {
      if (this._alive) this.load();
    }, ms));
  }
  loadAudiences() {
    if (this.state.audiences !== null) return;
    this.setState({
      audiences: []
    });
    dataSvc("/audience?$top=200", "audience").getAsync().then(r => {
      if (this._alive) this.setState({
        audiences: r && r.items || []
      });
    }).catch(() => {
      if (this._alive) this.setState({
        audiences: []
      });
    });
  }
  /** The feed's latest real run (source checks don't count toward its health). */
  lastRunFor(id) {
    return this.state.runs.find(r => r.scheduleId === id && !isCheckRun(r)) || null;
  }

  /** state: paused | never | running | failed | empty | overdue | ok */
  healthOf(row) {
    return feedHealth(row, this.state.runs);
  }
  renderForm() {
    const {
      form: f,
      providers,
      saving,
      validating,
      validation
    } = this.state;
    if (!f) return null;
    const prov = (providers || []).find(p => p.name === f.provider);
    const modes = prov && prov.modes || [];
    const zones = [browserTz(), f.timezone, ...TIMEZONES].filter((z, i, a) => z && a.indexOf(z) === i);
    const freqs = FREQUENCIES.some(([v]) => v === f.frequency) ? FREQUENCIES : FREQUENCIES.concat([[f.frequency, `${f.frequency} (runs daily)`]]);
    return /*#__PURE__*/React.createElement(Card, {
      span: 12,
      title: f.id ? "Change this feed" : "Add a feed",
      hint: "Check the file reads its first line before anything is saved"
    }, /*#__PURE__*/React.createElement("div", {
      className: "wa-form"
    }, /*#__PURE__*/React.createElement("label", null, "What the file holds", /*#__PURE__*/React.createElement("select", {
      value: f.provider,
      onChange: e => {
        const p = (providers || []).find(x => x.name === e.target.value);
        this.setState({
          form: {
            ...f,
            provider: e.target.value,
            mode: p && p.modes[0] || ""
          }
        });
      }
    }, /*#__PURE__*/React.createElement("option", {
      value: ""
    }, "Choose one"), (providers || []).map(p => /*#__PURE__*/React.createElement("option", {
      key: p.name,
      value: p.name
    }, feedName(providers, p.name))))), /*#__PURE__*/React.createElement("label", {
      className: "c2"
    }, "File address", /*#__PURE__*/React.createElement("input", {
      value: f.url,
      onChange: this.field("url"),
      placeholder: "https://\u2026/file.csv"
    })), /*#__PURE__*/React.createElement("label", null, "Sign-in name (optional)", /*#__PURE__*/React.createElement("input", {
      value: f.username,
      onChange: this.field("username"),
      autoComplete: "off"
    })), /*#__PURE__*/React.createElement("label", null, "Password (optional)", /*#__PURE__*/React.createElement("input", {
      type: "password",
      value: f.password,
      onChange: this.field("password"),
      placeholder: f._hadPassword ? "•••••• (unchanged)" : "",
      autoComplete: "new-password"
    })), /*#__PURE__*/React.createElement("label", null, "Each run", /*#__PURE__*/React.createElement("select", {
      value: f.mode || "",
      onChange: this.field("mode")
    }, modes.map(m => /*#__PURE__*/React.createElement("option", {
      key: m,
      value: m
    }, MODE_LABELS[m] || m)))), /*#__PURE__*/React.createElement("label", null, "How often", /*#__PURE__*/React.createElement("select", {
      value: f.frequency,
      onChange: this.field("frequency")
    }, freqs.map(([v, l]) => /*#__PURE__*/React.createElement("option", {
      key: v,
      value: v
    }, l)))), /*#__PURE__*/React.createElement("label", null, "At", /*#__PURE__*/React.createElement("select", {
      value: Number(f.hour) || 0,
      onChange: this.field("hour")
    }, Array.from({
      length: 24
    }, (_, h) => /*#__PURE__*/React.createElement("option", {
      key: h,
      value: h
    }, hourLabel(h))))), /*#__PURE__*/React.createElement("label", null, "Time zone", /*#__PURE__*/React.createElement("select", {
      value: f.timezone,
      onChange: this.field("timezone")
    }, zones.map(z => /*#__PURE__*/React.createElement("option", {
      key: z,
      value: z
    }, tzCity(z))))), /*#__PURE__*/React.createElement("label", null, "Status", /*#__PURE__*/React.createElement("select", {
      value: f.status,
      onChange: this.field("status")
    }, /*#__PURE__*/React.createElement("option", {
      value: "Active"
    }, "On"), /*#__PURE__*/React.createElement("option", {
      value: "Inactive"
    }, "Paused"))), f.provider === "Audience" ? (() => {
      this.loadAudiences();
      const SYSTEM = ["all contacts", "customers"];
      const opts = (this.state.audiences || []).filter(a => !SYSTEM.includes(String(a.name || "").toLowerCase()) || a.name === f.referenceId).map(a => a.name);
      if (f.referenceId && !opts.includes(f.referenceId)) opts.unshift(f.referenceId);
      return /*#__PURE__*/React.createElement("label", {
        className: "c2"
      }, "Add people to", /*#__PURE__*/React.createElement("select", {
        value: f.referenceId || "",
        onChange: this.field("referenceId")
      }, /*#__PURE__*/React.createElement("option", {
        value: ""
      }, "Choose an audience"), opts.map(n => /*#__PURE__*/React.createElement("option", {
        key: n,
        value: n
      }, n))), /*#__PURE__*/React.createElement("span", {
        className: "help"
      }, "People in the file join this audience. Built-in audiences fill themselves and can't be chosen."));
    })() : null), validation ? /*#__PURE__*/React.createElement(Notice, {
      tone: validation.valid ? "info" : "danger"
    }, /*#__PURE__*/React.createElement("span", {
      className: "grow"
    }, validation.valid ? /*#__PURE__*/React.createElement(React.Fragment, null, "The file reads fine: ", num((validation.columns || []).length), " columns", validation.columns ? ` (${validation.columns.slice(0, 10).join(", ")}${validation.columns.length > 10 ? "…" : ""})` : "") : validation.error || "The file couldn't be read.")) : null, /*#__PURE__*/React.createElement("div", {
      className: "wa-form-actions"
    }, /*#__PURE__*/React.createElement(Btn, {
      disabled: validating || !f.url,
      onClick: this.validate
    }, validating ? "Checking…" : "Check the file"), /*#__PURE__*/React.createElement(Btn, {
      primary: true,
      disabled: saving || !f.provider || !f.url,
      onClick: this.save
    }, saving ? "Saving…" : "Save"), /*#__PURE__*/React.createElement(Btn, {
      onClick: () => this.setState({
        form: null,
        validation: null
      })
    }, "Cancel")));
  }
  lastRunLine(row, h) {
    const p = row.provider;
    const run = h.run;
    if (!run) return {
      tone: "dim",
      text: this.state.runs.length >= RUNS_TOP ? "No recent runs" : "Never run"
    };
    const at = whenPhrase(run.finishedDate || run.startedDate);
    const count = COUNTLESS.includes(p) ? "" : ` · ${counted(p, Number(run.rowsUpserted) || 0)}`;
    switch (h.state) {
      case "running":
        return {
          tone: "dim",
          text: `Running now, started ${whenPhrase(run.startedDate)}`
        };
      case "failed":
        return {
          tone: "warn",
          text: `Ran ${at} but didn't finish, so nothing changed`,
          title: run.message
        };
      case "empty":
        return {
          tone: "warn",
          text: `Ran ${at} but the file was empty · 0 ${noun(p, 2)}`
        };
      case "overdue":
        return {
          tone: "warn",
          text: `Last updated ${at}, and it hasn't run since${count}`
        };
      case "paused":
        return {
          tone: "dim",
          text: `Last updated ${at}${count}`
        };
      default:
        return {
          tone: "ok",
          text: `Updated ${at}${count}`
        };
    }
  }
  issueSentence(row, h) {
    const name = feedName(this.state.providers, row.provider);
    const at = h.run ? whenPhrase(h.run.finishedDate || h.run.startedDate) : "";
    if (h.state === "empty") return `${name} came in with 0 ${noun(row.provider, 2)} ${at}, so nothing was refreshed.`;
    if (h.state === "failed") return `${name} didn't finish ${at}, so nothing changed. Run it again, or use Change to check the file address.`;
    if (h.state === "overdue") return `${name} was last updated ${at} and hasn't run since.`;
    return "";
  }
  renderSummary(items) {
    const count = st => items.filter(x => st.includes(x.h.state)).length;
    const ok = count(["ok", "running"]);
    const issues = items.filter(x => ["failed", "empty", "overdue"].includes(x.h.state));
    const never = count(["never"]);
    const paused = count(["paused"]);
    const parts = [];
    if (ok) parts.push(ok === items.length ? ok === 1 ? "Your feed is up to date." : `All ${ok} feeds are up to date.` : `${ok} ${ok === 1 ? "feed is" : "feeds are"} up to date.`);
    if (issues.length) parts.push(`${issues.length} ${issues.length === 1 ? "needs" : "need"} a look.`);
    if (never) parts.push(`${never} ${never === 1 ? "hasn't" : "haven't"} run yet.`);
    if (paused) parts.push(`${paused} ${paused === 1 ? "is" : "are"} paused.`);
    const first = issues[0];
    const more = issues.length - 1;
    const sub = first ? `${this.issueSentence(first.row, first.h)}${more > 0 ? ` ${more} more ${more === 1 ? "feed needs" : "feeds need"} a look too.` : ""}` : `Each feed runs on its own schedule. Times here are in ${tzCity(browserTz())}.`;
    const tone = issues.length ? "warn" : ok ? "ok" : "idle";
    return /*#__PURE__*/React.createElement("section", {
      className: "df-summary"
    }, /*#__PURE__*/React.createElement("span", {
      className: `df-dot lg ${tone}`
    }), /*#__PURE__*/React.createElement("div", {
      className: "df-summary-t"
    }, /*#__PURE__*/React.createElement("b", null, parts.join(" ")), /*#__PURE__*/React.createElement("span", null, sub)), first ? /*#__PURE__*/React.createElement(Btn, {
      onClick: () => this.showRuns(first.row.id)
    }, "See what happened") : null);
  }
  renderFeedCard({
    row,
    h
  }) {
    const {
      providers,
      running,
      toggling,
      confirmDelete
    } = this.state;
    const s = parseSettings(row);
    const name = feedName(providers, row.provider);
    const mode = row.mode || (((providers || []).find(p => p.name === row.provider) || {}).modes || [])[0] || "";
    const what = modeText(row.provider, mode, s);
    const line = this.lastRunLine(row, h);
    const warn = ["failed", "empty", "overdue"].includes(h.state);
    const dot = warn ? "warn" : h.state === "ok" || h.state === "running" ? "ok" : "idle";
    return /*#__PURE__*/React.createElement("section", {
      key: row.id,
      className: `df-feed${warn ? " warn" : ""}`
    }, /*#__PURE__*/React.createElement("div", {
      className: "df-feed-h"
    }, /*#__PURE__*/React.createElement("span", {
      className: `df-dot ${dot}`
    }), /*#__PURE__*/React.createElement("b", {
      className: "df-feed-name"
    }, name), warn ? /*#__PURE__*/React.createElement(Pill, {
      tone: "warning"
    }, "Needs a look") : h.state === "paused" ? /*#__PURE__*/React.createElement(Pill, {
      tone: "neutral"
    }, "Paused") : h.state === "running" ? /*#__PURE__*/React.createElement(Pill, {
      tone: "info"
    }, "Running") : null, /*#__PURE__*/React.createElement(RowMenu, {
      label: name,
      items: [{
        label: running[row.id] ? "Starting…" : "Run now",
        disabled: !!running[row.id],
        onClick: () => this.runNow(row)
      }, {
        label: "Change",
        onClick: () => this.startEdit(row)
      }, {
        label: row.status === "Active" ? "Pause" : "Resume",
        disabled: !!toggling[row.id],
        onClick: () => this.togglePause(row)
      }, {
        label: "See its runs",
        onClick: () => this.showRuns(row.id)
      }, {
        label: "Delete feed",
        danger: true,
        onClick: () => this.setState({
          confirmDelete: row.id
        })
      }]
    })), /*#__PURE__*/React.createElement("span", {
      className: "df-feed-what"
    }, "From ", sourceText(row, s), ", ", scheduleText(row), ".", what ? ` ${what}` : ""), /*#__PURE__*/React.createElement("span", {
      className: `df-feed-last ${line.tone}`,
      title: line.title || undefined
    }, line.text), confirmDelete === row.id ? /*#__PURE__*/React.createElement("div", {
      className: "df-confirm"
    }, /*#__PURE__*/React.createElement("span", null, "Delete this feed? It stops running. Data it already brought in stays."), /*#__PURE__*/React.createElement(Btn, {
      small: true,
      danger: true,
      onClick: () => this.remove(row)
    }, "Delete feed"), /*#__PURE__*/React.createElement(Btn, {
      small: true,
      onClick: () => this.setState({
        confirmDelete: null
      })
    }, "Keep")) : null);
  }
  renderRuns() {
    const {
      providers,
      runs,
      schedules,
      runsFor
    } = this.state;
    const filtered = (runsFor ? runs.filter(r => r.scheduleId === runsFor) : runs).slice(0, RUNS_SHOWN);
    const forRow = runsFor ? (schedules || []).find(s => s.id === runsFor) : null;
    const right = runsFor ? /*#__PURE__*/React.createElement("span", {
      className: "df-runs-filter"
    }, forRow ? `${feedName(providers, forRow.provider)} only` : "One feed only", " \xB7 ", /*#__PURE__*/React.createElement("a", {
      onClick: () => this.setState({
        runsFor: null
      })
    }, "Show all")) : null;
    return /*#__PURE__*/React.createElement("div", {
      ref: el => {
        this._runsEl = el;
      }
    }, /*#__PURE__*/React.createElement(Card, {
      span: 12,
      title: "Recent runs",
      hint: `last ${RUNS_SHOWN}`,
      right: right
    }, !filtered.length ? /*#__PURE__*/React.createElement(Empty, null, runsFor ? "This feed hasn't run yet. Use Run now from its menu to try it." : "No runs yet. Runs show up here as feeds run on their schedule or from Run now.") : /*#__PURE__*/React.createElement("div", {
      className: "tblwrap"
    }, /*#__PURE__*/React.createElement("table", {
      className: "territory-table wa-table df-runs"
    }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", {
      style: {
        width: 170
      }
    }, "When"), /*#__PURE__*/React.createElement("th", {
      style: {
        width: 170
      }
    }, "Feed"), /*#__PURE__*/React.createElement("th", {
      style: {
        width: 130
      }
    }, "Result"), /*#__PURE__*/React.createElement("th", null, "What changed"))), /*#__PURE__*/React.createElement("tbody", null, filtered.map(r => {
      const [tone, label] = runResult(r);
      return /*#__PURE__*/React.createElement("tr", {
        key: r.id,
        title: r.message || undefined
      }, /*#__PURE__*/React.createElement("td", null, whenCell(r.startedDate)), /*#__PURE__*/React.createElement("td", null, feedName(providers, r.provider)), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement(Pill, {
        tone: tone
      }, label)), /*#__PURE__*/React.createElement("td", {
        className: "wrap"
      }, whatChanged(r)));
    }))))));
  }
  render() {
    const {
      providers,
      schedules,
      error,
      notice,
      form
    } = this.state;
    const loading = schedules === null || providers === null;
    const items = loading ? [] : schedules.map(row => ({
      row,
      h: this.healthOf(row)
    }));
    return /*#__PURE__*/React.createElement(DataShell, {
      active: "/Data",
      cls: "df-view",
      actions: !form ? /*#__PURE__*/React.createElement(Btn, {
        primary: true,
        onClick: this.startAdd
      }, "Add a feed") : null
    }, error ? /*#__PURE__*/React.createElement(Error, null) : null, notice ? /*#__PURE__*/React.createElement(Notice, {
      onClose: () => this.setState({
        notice: null
      })
    }, /*#__PURE__*/React.createElement("span", {
      className: "grow"
    }, notice)) : null, form ? /*#__PURE__*/React.createElement("div", {
      className: "wa-grid df-form"
    }, this.renderForm()) : null, loading && !error ? /*#__PURE__*/React.createElement("div", {
      className: "df-stack"
    }, /*#__PURE__*/React.createElement(Pending, {
      h: 74
    }), /*#__PURE__*/React.createElement(Pending, {
      h: 220
    })) : null, !loading && !items.length ? /*#__PURE__*/React.createElement("div", {
      className: "wa-grid"
    }, /*#__PURE__*/React.createElement(Card, {
      span: 12
    }, /*#__PURE__*/React.createElement(Empty, null, "No feeds yet. A feed brings in a file on a schedule, such as your products, orders, customers or a list of people. Choose Add a feed to set one up."))) : null, !loading && items.length ? /*#__PURE__*/React.createElement("div", {
      className: "df-stack"
    }, this.renderSummary(items), /*#__PURE__*/React.createElement("div", {
      className: "df-grid"
    }, items.map(x => this.renderFeedCard(x))), this.renderRuns()) : null);
  }
}
export default withRouter(FeedsView);